import { supabase } from '../config/supabaseClient';
import { getReferralCommission } from './settingsService';

/**
 * Get the current user's referral code
 */
export const getReferralCode = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('referral_code')
    .eq('id', userId)
    .single();

  if (error) return { referralCode: null, error: error.message };
  return { referralCode: data?.referral_code, error: null };
};

/**
 * Get referral stats for the current user
 */
export const getReferralStats = async (userId) => {
  // Count users referred by this user
  const { data: referrals, error } = await supabase
    .from('users')
    .select('id, full_name, email, created_at')
    .eq('referred_by', userId)
    .order('created_at', { ascending: false });

  if (error) return { stats: null, error: error.message };

  // Count referral bonus transactions. from_user_id is the referred user who
  // triggered the bonus, so it maps each payment back to a specific referral.
  const [bonusResult, currentCommission] = await Promise.all([
    supabase
      .from('wallet_transactions')
      .select('amount_usd, from_user_id, created_at')
      .eq('to_user_id', userId)
      .eq('transaction_type', 'referral_bonus')
      .eq('status', 'confirmed'),
    getReferralCommission(),
  ]);

  const { data: bonuses, error: bonusError } = bonusResult;
  const totalEarned = bonuses?.reduce((sum, tx) => sum + Number(tx.amount_usd || 0), 0) || 0;

  // Sum per referred user: a referral can in principle have more than one
  // bonus row, and one that predates the bonus feature has none at all.
  const bonusByReferral = new Map();
  (bonuses || []).forEach((tx) => {
    if (!tx.from_user_id) return;
    const prev = bonusByReferral.get(tx.from_user_id);
    bonusByReferral.set(tx.from_user_id, {
      amount: (prev?.amount || 0) + Number(tx.amount_usd || 0),
      paidAt: prev?.paidAt || tx.created_at,
    });
  });

  // The bonus query failing must not discard the referral list we did get. Callers
  // guard on `error` before using `stats`, so reporting a partial failure here would
  // render "0 referrals / $0.00 earned" as if it were fact. Surface it separately.
  return {
    stats: {
      totalReferrals: referrals?.length || 0,
      totalEarned,
      bonusError: bonusError?.message || null,
      // bonusEarned is the amount actually credited for this referral, which
      // is not necessarily the current commission rate.
      referrals: (referrals || []).map((r) => ({
        ...r,
        bonusEarned: bonusByReferral.get(r.id)?.amount ?? null,
        bonusPaidAt: bonusByReferral.get(r.id)?.paidAt ?? null,
      })),
      currentCommission,
    },
    error: null,
  };
};

/**
 * Generate shareable referral link
 */
export const getReferralLink = (referralCode) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/sign-up?ref=${referralCode}`;
};

/**
 * Get system-wide referral statistics (admin only)
 */
export const getSystemReferralStats = async () => {
  try {
    const { data, error } = await supabase.rpc('get_system_referral_stats');
    if (error) throw error;
    return {
      success: true,
      data: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    console.error('Error fetching system referral stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get per-user referral summary for admin table view
 */
export const getAllUsersReferralSummary = async () => {
  try {
    const { data, error } = await supabase.rpc('get_all_users_referral_summary');
    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching users referral summary:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Per-referral breakdown for one referrer (admin only).
 *
 * Returns one row per referred user with their verification state and the bonus
 * row attached to them. Verification lives in auth.users.email_confirmed_at,
 * which PostgREST cannot reach, so this has to go through the RPC.
 *
 * The caller aggregates its own totals from these rows rather than issuing a
 * second count query — that way the summary and the table can never disagree.
 */
export const getReferralDetail = async (userId) => {
  try {
    const { data, error } = await supabase.rpc('get_referral_detail', { p_user_id: userId });
    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching referral detail:', error);
    return { success: false, error: error.message };
  }
};
